import React, { useCallback, useState } from 'react';
import { Alert, Box, Button, FormField, Icon, Input, Modal, SpaceBetween, Table } from '@amzn/awsui-components-core';
import { Employee, AttendeePriority } from '../../interfaces/interface';
import { UserEAConfig } from '../../services/UserEAConfigService';
import { EmployeeService } from '../../services/EmployeeService';
import { EmployeeListItem } from '../attendees/EmployeeListItem';
import { EmployeeSearchBar } from '../attendees/EmployeeSearchBar';
import { LeaderCell } from '../attendees/LeaderCell';
import { useEmployeeByAlias } from '../../hooks/useEmployeeByAlias';
import styles from './Sidebar.module.scss';

// ══════════════════════════════════════════════════════════════════════════════
// MySettingsSection
//
// Self-service entry point for per-user EA Coverage overrides. Lives in the
// sidebar below My Meetings. Owns the manage modal lifecycle plus add/edit
// and delete confirmation modals.
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Read-only inline employee row used in the Edit Override modal — shows the
 * leader as an EmployeeListItem since the alias is the row key and can't be
 * changed in edit mode. Falls back to a disabled Input if the employee record
 * cannot be resolved.
 */
const LeaderEmployeeRow: React.FC<{ alias: string }> = ({ alias }) => {
  const employee = useEmployeeByAlias(alias);
  if (employee) {
    return <EmployeeListItem employee={employee} disableHover />;
  }
  return <Input value={alias} onChange={() => undefined} disabled />;
};

export interface MySettingsSectionProps {
  userEAConfigs: UserEAConfig[];
  onSaveUserEAConfig?: (config: UserEAConfig) => Promise<void>;
  onDeleteUserEAConfig?: (leaderAlias: string) => Promise<void>;
}

export const MySettingsSection: React.FC<MySettingsSectionProps> = ({ userEAConfigs, onSaveUserEAConfig, onDeleteUserEAConfig }) => {
  // Modal visibility
  const [showManageModal, setShowManageModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<UserEAConfig | null>(null);

  // Edit/Add modal state
  const [editingConfig, setEditingConfig] = useState<UserEAConfig | null>(null);
  const [leaderAlias, setLeaderAlias] = useState('');
  const [eaLogin, setEaLogin] = useState('');
  const [autoPopulatedEaLogin, setAutoPopulatedEaLogin] = useState('');
  const [selectedLeader, setSelectedLeader] = useState<Employee | null>(null);
  const [selectedEa, setSelectedEa] = useState<Employee | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isLoadingDefaultEa, setIsLoadingDefaultEa] = useState(false);

  // Delete modal state
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // ── Add / Edit handlers ────────────────────────────────────────────────────

  const resetEditForm = () => {
    setEditingConfig(null);
    setLeaderAlias('');
    setEaLogin('');
    setAutoPopulatedEaLogin('');
    setSelectedLeader(null);
    setSelectedEa(null);
    setSaveError(null);
    setIsLoadingDefaultEa(false);
  };

  const handleAddOverride = () => {
    resetEditForm();
    // Manage modal stays open underneath — Add/Edit overlays on top so the
    // user returns to the table naturally on close/save.
    setShowEditModal(true);
  };

  const handleEditOverride = (config: UserEAConfig) => {
    resetEditForm();
    setEditingConfig(config);
    setLeaderAlias(config.leaderAlias);
    setEaLogin(config.eaLogin ?? '');
    setShowEditModal(true);
    setIsLoadingDefaultEa(true);

    // Pre-resolve the EA's full record so the EmployeeListItem renders with
    // the same close-icon UX as Add mode. Also fetch the PAPI-default EA so
    // the description hint can show when the user reverts to the default.
    if (config.eaLogin) {
      EmployeeService.getEmployeeByLogin(config.eaLogin)
        .then((emp) => emp && setSelectedEa(emp))
        .catch(() => {
          /* fall back to disabled-input rendering */
        });
    }
    EmployeeService.getEmployeeByLogin(config.leaderAlias)
      .then((leader) => (leader ? EmployeeService.findExecutiveAssistant(leader) : null))
      .then((defaultEa) => {
        if (defaultEa?.login_name) setAutoPopulatedEaLogin(defaultEa.login_name);
      })
      .catch(() => {
        /* default lookup is best-effort for the description hint */
      })
      .finally(() => setIsLoadingDefaultEa(false));
  };

  /**
   * When a leader is selected via search, auto-populate the EA Override field
   * with the leader's PAPI-default EA. The user can keep this default (it's
   * stripped on save), pick a different EA, or clear it entirely.
   */
  const handleLeaderSelected = useCallback(async (leader: Employee) => {
    setSelectedLeader(leader);
    setLeaderAlias(leader.login_name);
    setIsLoadingDefaultEa(true);
    try {
      const defaultEa = await EmployeeService.findExecutiveAssistant(leader);
      if (defaultEa) {
        setSelectedEa(defaultEa);
        setEaLogin(defaultEa.login_name);
        setAutoPopulatedEaLogin(defaultEa.login_name);
      } else {
        setSelectedEa(null);
        setEaLogin('');
        setAutoPopulatedEaLogin('');
      }
    } catch {
      setSelectedEa(null);
      setEaLogin('');
      setAutoPopulatedEaLogin('');
    } finally {
      setIsLoadingDefaultEa(false);
    }
  }, []);

  const handleEaSelected = useCallback((ea: Employee) => {
    setSelectedEa(ea);
    setEaLogin(ea.login_name);
  }, []);

  const handleClearLeader = useCallback(() => {
    setSelectedLeader(null);
    setLeaderAlias('');
    setSelectedEa(null);
    setEaLogin('');
    setAutoPopulatedEaLogin('');
  }, []);

  const handleClearEa = useCallback(() => {
    setSelectedEa(null);
    setEaLogin('');
  }, []);

  const handleSaveOverride = async () => {
    setSaveError(null);
    if (!leaderAlias.trim()) {
      setSaveError('Leader alias is required.');
      return;
    }
    if (!onSaveUserEAConfig) return;
    setSaving(true);
    try {
      const trimmedEa = eaLogin.trim().toLowerCase();
      // Strip eaLogin if it matches the auto-populated PAPI default — the
      // user didn't explicitly choose to override the EA person, so let
      // resolution fall through to PAPI default at render time.
      const eaLoginToSave = trimmedEa && trimmedEa !== autoPopulatedEaLogin.toLowerCase() ? trimmedEa : undefined;
      await onSaveUserEAConfig({
        leaderAlias: leaderAlias.trim().toLowerCase(),
        eaLogin: eaLoginToSave,
        updatedAt: new Date().toISOString()
      });
      setShowEditModal(false);
    } catch {
      setSaveError('Failed to save override.');
    } finally {
      setSaving(false);
    }
  };

  // ── Delete handler ─────────────────────────────────────────────────────────

  const handleConfirmDelete = async () => {
    if (!pendingDelete || !onDeleteUserEAConfig) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      await onDeleteUserEAConfig(pendingDelete.leaderAlias);
      setPendingDelete(null);
    } catch {
      setDeleteError('Failed to delete override. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  // ── Save-button gating ─────────────────────────────────────────────────────
  // Save is meaningful only when the EA selection actually changes the
  // persisted state.
  // - Add mode: user must pick an EA different from the auto-populated PAPI default.
  // - Edit mode: user must change the EA from what's currently stored AND the
  //   new value must not match the auto-populated default (which would be
  //   stripped on save, resulting in a no-op).
  const currentEaLogin = eaLogin.trim().toLowerCase();
  const noLeader = !leaderAlias.trim();
  let isNoOpSave: boolean;
  if (editingConfig) {
    const originalEaLogin = (editingConfig.eaLogin ?? '').toLowerCase();
    isNoOpSave = !currentEaLogin || currentEaLogin === originalEaLogin || currentEaLogin === autoPopulatedEaLogin.toLowerCase();
  } else {
    isNoOpSave = !currentEaLogin || currentEaLogin === autoPopulatedEaLogin.toLowerCase();
  }
  const isSaveDisabled = noLeader || isNoOpSave || isLoadingDefaultEa;

  return (
    <>
      <div className={styles.settingsSection}>
        <div className={styles.settingsSectionContent}>
          <div className={styles.settingsSectionHeader}>
            <Icon name="settings" size="small" />
            <p className={styles.settingsSectionHeading}>My Settings</p>
          </div>
          <button
            type="button"
            className={styles.manageEaCoverageBtn}
            onClick={() => setShowManageModal(true)}
            aria-label="Manage EA Coverage"
            title="Manage EA Coverage"
          >
            Manage EA Coverage ({userEAConfigs.length})
          </button>
        </div>
      </div>

      {/* Manage Overrides modal — table view of all user EA overrides */}
      {showManageModal && (
        <Modal
          visible={true}
          onDismiss={() => setShowManageModal(false)}
          header={`Manage EA Coverage (${userEAConfigs.length})`}
          size="large"
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button variant="link" onClick={() => setShowManageModal(false)}>
                  Close
                </Button>
                <Button variant="primary" iconName="add-plus" onClick={handleAddOverride}>
                  Add Override
                </Button>
              </SpaceBetween>
            </Box>
          }
        >
          <Table
            variant="embedded"
            items={userEAConfigs}
            empty={
              <Box textAlign="center" color="text-body-secondary" padding={{ vertical: 'l' }}>
                <SpaceBetween direction="vertical" size="s">
                  <Box variant="p">You haven&apos;t created any overrides yet.</Box>
                  <Box variant="p" fontSize="body-s">
                    Overrides let you customize the Executive Assistant for specific leaders across all your meetings.
                  </Box>
                </SpaceBetween>
              </Box>
            }
            columnDefinitions={[
              {
                id: 'leaderAlias',
                header: 'Leader',
                width: 280,
                cell: (item: UserEAConfig) => <LeaderCell alias={item.leaderAlias} />
              },
              {
                id: 'eaLogin',
                header: 'Executive Assistant',
                width: 280,
                cell: (item: UserEAConfig) =>
                  item.eaLogin ? (
                    <LeaderCell alias={item.eaLogin} />
                  ) : (
                    <Box color="text-body-secondary" fontSize="body-s">
                      —
                    </Box>
                  )
              },
              {
                id: 'actions',
                header: 'Actions',
                width: 110,
                cell: (item: UserEAConfig) => (
                  <SpaceBetween direction="horizontal" size="xxs">
                    <Button
                      variant="icon"
                      iconName="edit"
                      ariaLabel={`Edit override for ${item.leaderAlias}`}
                      onClick={() => handleEditOverride(item)}
                    />
                    <Button
                      variant="icon"
                      iconName="remove"
                      ariaLabel={`Delete override for ${item.leaderAlias}`}
                      onClick={() => setPendingDelete(item)}
                    />
                  </SpaceBetween>
                )
              }
            ]}
          />
        </Modal>
      )}

      {/* Delete override confirmation modal */}
      {pendingDelete && (
        <Modal
          visible={true}
          onDismiss={() => {
            setPendingDelete(null);
            setDeleteError(null);
          }}
          header="Delete EA Override"
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button
                  variant="link"
                  disabled={isDeleting}
                  onClick={() => {
                    setPendingDelete(null);
                    setDeleteError(null);
                  }}
                >
                  Cancel
                </Button>
                <Button variant="primary" onClick={handleConfirmDelete} loading={isDeleting} disabled={isDeleting}>
                  Delete
                </Button>
              </SpaceBetween>
            </Box>
          }
        >
          <SpaceBetween direction="vertical" size="s">
            {deleteError && <Alert type="error">{deleteError}</Alert>}
            <Alert type="warning" statusIconAriaLabel="Warning">
              This will revert to the default EA configuration for this leader across all your meetings.
            </Alert>
            <Box>
              Delete override for <b>{pendingDelete.leaderAlias}</b>?
            </Box>
          </SpaceBetween>
        </Modal>
      )}

      {/* Add / Edit Override modal */}
      {showEditModal && (
        <Modal
          visible={true}
          onDismiss={() => {
            setShowEditModal(false);
            setSaveError(null);
          }}
          header={editingConfig ? 'Edit EA Override' : 'Add EA Override'}
          size="medium"
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button
                  variant="link"
                  onClick={() => {
                    setShowEditModal(false);
                    setSaveError(null);
                  }}
                >
                  Cancel
                </Button>
                <Button variant="primary" onClick={handleSaveOverride} loading={saving} disabled={isSaveDisabled}>
                  Save
                </Button>
              </SpaceBetween>
            </Box>
          }
        >
          <SpaceBetween size="m">
            {saveError && <Alert type="error">{saveError}</Alert>}
            <FormField label="Leader Alias">
              {editingConfig ? (
                <LeaderEmployeeRow alias={leaderAlias} />
              ) : selectedLeader ? (
                <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                  <EmployeeListItem employee={selectedLeader} disableHover />
                  <Button variant="icon" iconName="close" ariaLabel="Remove selected leader" onClick={handleClearLeader} />
                </SpaceBetween>
              ) : (
                <EmployeeSearchBar
                  onAddAttendee={(employee: Employee, _priority: AttendeePriority) => handleLeaderSelected(employee)}
                  existingEmails={new Set()}
                  singleAddButton={true}
                  addButtonText="Select"
                  placeholder="Search by name or alias..."
                  fullWidth={true}
                />
              )}
            </FormField>
            <FormField
              label="EA Override"
              description={eaLogin && eaLogin === autoPopulatedEaLogin ? 'Default EA, click on the ✕ to identify an alternate.' : undefined}
            >
              {selectedEa ? (
                <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                  <EmployeeListItem employee={selectedEa} disableHover />
                  <Button variant="icon" iconName="close" ariaLabel="Remove selected EA" onClick={handleClearEa} />
                </SpaceBetween>
              ) : editingConfig || selectedLeader ? (
                <EmployeeSearchBar
                  onAddAttendee={(employee: Employee, _priority: AttendeePriority) => handleEaSelected(employee)}
                  existingEmails={new Set()}
                  singleAddButton={true}
                  addButtonText="Select"
                  placeholder="Search by name or alias to override..."
                  fullWidth={true}
                />
              ) : (
                <Input value="" disabled placeholder="Select a leader first..." />
              )}
            </FormField>
          </SpaceBetween>
        </Modal>
      )}
    </>
  );
};
